import { useState, useEffect, useCallback } from "react";
import api, { fmtMoney, fmtDate, errDetail } from "../lib/api";
import { DataTable } from "../components/DataTable";
import { PageHeader } from "../components/CrudPage";
import { StatCard } from "../components/StatCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Wallet, Banknote, CreditCard, Landmark } from "lucide-react";
import { toast } from "sonner";

const METHOD_LABELS = { nakit: "Nakit", kredi_karti: "Kredi Kartı", havale: "Havale" };

export default function Cash() {
  const [summary, setSummary] = useState(null);
  const [dayEnd, setDayEnd] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ type: "", method: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cash/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/cash/day-end").then((r) => setDayEnd(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/cash/transactions", { params: { page, limit: 15, ...filters } });
      setData(d);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="cash-page">
      <PageHeader title="Kasa" subtitle="Kasa bakiyeleri, hareketler ve gün sonu raporu" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="cash-total" label="Toplam Bakiye" value={fmtMoney(summary?.total || 0)} icon={Wallet} accent />
        <StatCard testId="cash-nakit" label="Nakit" value={fmtMoney(summary?.balances?.nakit || 0)} icon={Banknote} />
        <StatCard testId="cash-kart" label="Kart" value={fmtMoney(summary?.balances?.kredi_karti || 0)} icon={CreditCard} />
        <StatCard testId="cash-havale" label="Havale" value={fmtMoney(summary?.balances?.havale || 0)} icon={Landmark} />
      </div>

      <Tabs defaultValue="transactions" className="mt-8">
        <TabsList className="rounded-none bg-white p-0 border border-gray-200 h-auto">
          <TabsTrigger data-testid="cash-tab-transactions" value="transactions"
            className="rounded-none px-6 py-2.5 data-[state=active]:bg-[#0B1F3A] data-[state=active]:text-white">
            Kasa Hareketleri
          </TabsTrigger>
          <TabsTrigger data-testid="cash-tab-dayend" value="dayend"
            className="rounded-none px-6 py-2.5 data-[state=active]:bg-[#0B1F3A] data-[state=active]:text-white">
            Gün Sonu Raporu
          </TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="mt-4">
          <div className="mb-4 flex flex-wrap gap-3">
            <Select value={filters.type || "all"} onValueChange={(v) => { setPage(1); setFilters({ ...filters, type: v === "all" ? "" : v }); }}>
              <SelectTrigger data-testid="cash-type-filter" className="w-40 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">İşlem: Tümü</SelectItem>
                <SelectItem value="giris">Giriş</SelectItem>
                <SelectItem value="cikis">Çıkış</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.method || "all"} onValueChange={(v) => { setPage(1); setFilters({ ...filters, method: v === "all" ? "" : v }); }}>
              <SelectTrigger data-testid="cash-method-filter" className="w-40 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Yöntem: Tümü</SelectItem>
                {Object.entries(METHOD_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DataTable testId="cash-transactions-table" columns={[
            { key: "created_at", label: "Tarih", render: (r) => fmtDate(r.created_at) },
            {
              key: "type", label: "İşlem",
              render: (r) => (
                <span className={`px-2 py-1 text-xs font-semibold ${r.type === "giris" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                  {r.type === "giris" ? "Giriş" : "Çıkış"}
                </span>
              ),
            },
            { key: "method", label: "Yöntem", render: (r) => METHOD_LABELS[r.method] || r.method },
            {
              key: "amount", label: "Tutar",
              render: (r) => (
                <span className={`font-semibold ${r.type === "giris" ? "text-emerald-600" : "text-red-500"}`}>
                  {r.type === "giris" ? "+" : "-"}{fmtMoney(r.amount)}
                </span>
              ),
            },
            { key: "description", label: "Açıklama" },
            { key: "user_name", label: "Kullanıcı" },
          ]} data={data.items} loading={loading} page={page} pages={data.pages} total={data.total} onPage={setPage} />
        </TabsContent>

        <TabsContent value="dayend" className="mt-4">
          {dayEnd && (
            <div className="border border-gray-200 bg-white p-6" data-testid="day-end-report">
              <h3 className="font-heading text-lg font-semibold">Gün Sonu Raporu · {dayEnd.date}</h3>
              <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="border border-gray-200 p-4">
                  <p className="text-xs uppercase text-gray-500">Toplam Giriş</p>
                  <p className="mt-1 font-heading text-xl font-semibold text-emerald-600">{fmtMoney(dayEnd.total_in)}</p>
                </div>
                <div className="border border-gray-200 p-4">
                  <p className="text-xs uppercase text-gray-500">Toplam Çıkış</p>
                  <p className="mt-1 font-heading text-xl font-semibold text-red-500">{fmtMoney(dayEnd.total_out)}</p>
                </div>
                <div className="border border-gray-200 p-4">
                  <p className="text-xs uppercase text-gray-500">Net</p>
                  <p className="mt-1 font-heading text-xl font-semibold text-[#D4AF37]">{fmtMoney(dayEnd.net)}</p>
                </div>
                <div className="border border-gray-200 p-4">
                  <p className="text-xs uppercase text-gray-500">Satış Adedi</p>
                  <p className="mt-1 font-heading text-xl font-semibold">{dayEnd.sales_count}</p>
                </div>
              </div>
              <table className="mt-6 w-full border border-gray-200 text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <th className="px-4 py-2">Yöntem</th><th className="px-4 py-2">Giriş</th><th className="px-4 py-2">Çıkış</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(dayEnd.methods).length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-gray-400">Bugün işlem yok</td></tr>
                  )}
                  {Object.entries(dayEnd.methods).map(([m, v]) => (
                    <tr key={m} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-medium">{METHOD_LABELS[m] || m}</td>
                      <td className="px-4 py-2 text-emerald-600">{fmtMoney(v.giris)}</td>
                      <td className="px-4 py-2 text-red-500">{fmtMoney(v.cikis)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
